import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { DoctorTrainingCard } from "@/components/doctor-training/doctor-training-card/doctor-training-card";
import { CardList } from "@/components/doctor-training/card-list";

import { scrollToPos } from "@/service/scroll/scroll";
import { InfoWithCta } from "@/components/common/info-with-cta";
import { LoaderIcon, SearchIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SendMedicalFacility } from "@/components/doctor-training/send-medical-facility/send-medical-facility";

function sliceToFacilities(facilities, max, geoFilter) {
  const list = [];

  if (!geoFilter) {
    return facilities.slice(0, max);
  }

  for (let i = 0; i < facilities.length; i++) {
    const facility = facilities[i];

    const coords = facility.coordinates;

    const inArea =
      coords.latitude <= geoFilter.topLeft.lat &&
      coords.latitude >= geoFilter.bottomRight.lat &&
      coords.longitude >= geoFilter.topLeft.lng &&
      coords.longitude <= geoFilter.bottomRight.lng;

    if (inArea) {
      list.push(facility);

      if (list.length === max) {
        return list;
      }
    }
  }

  return list;
}

export const LazyList = ({
  facilities,
  geoFilter,
  resetMap,
  setupType,
  inFavourite,
  filterSource,
}: {
  facilities: MedicalFacilityCard[];
  geoFilter: { topLeft: Coordinate; bottomRight: Coordinate } | null;
  resetMap: (reset: boolean) => void;
  setupType: DoctorTrainingType;
  inFavourite: boolean;
  filterSource: InternshipMapFilterSource | ResidencyMapFilterSource;
}) => {
  const [shown, setShown] = useState(() =>
    sliceToFacilities(facilities, 24, geoFilter),
  );
  const { ref, inView } = useInView({ rootMargin: "0px" });

  useEffect(() => {
    const newShown = sliceToFacilities(
      facilities,
      shown.length > 24 ? shown.length : 24,
      geoFilter,
    );

    if (newShown.length < shown.length && window.innerWidth > 768) {
      const offset =
        document.querySelector("#dashboard-content").getBoundingClientRect()
          .top +
        window.scrollY +
        100;

      if (offset < window.scrollY) {
        setTimeout(() => {
          scrollToPos({
            top: offset,
            behavior: "instant",
          });
        }, 100);
      }
    }

    setShown(newShown);
  }, [facilities, geoFilter]);

  useEffect(() => {
    if (inView && shown.length < facilities.length) {
      setShown((prevShown) =>
        sliceToFacilities(facilities, prevShown.length + 24, geoFilter),
      );
    }
  }, [inView]);

  return (
    <CardList empty={facilities.length === 0}>
      {shown.map((facility: MedicalFacilityCard, index) => (
        <DoctorTrainingCard
          key={`${facility.title}, ${index}`}
          type={setupType}
          facility={facility}
        />
      ))}
      {facilities.length === 0 && (
        <InfoWithCta
          icon={<SearchIcon size={"24px"} />}
          title={inFavourite ? "Eksploruj" : "Pusto!"}
          desc={
            inFavourite
              ? "Brak zapisanych placówek"
              : "Brak placówek spełniających wybrane kryteria. Spróbuj zmienić filtry."
          }
          cta={
            inFavourite ? (
              <Button
                variant={"blue"}
                onClick={() => {
                  window.location.href = window.location.pathname
                    .split("/")
                    .slice(0, 2)
                    .join("/");
                }}
              >
                Wróć do mapy
              </Button>
            ) : setupType === "residency" ? (
              <SendMedicalFacility
                filterSource={filterSource as ResidencyMapFilterSource}
              />
            ) : (
              <></>
            )
          }
        />
      )}

      {facilities.length > 0 && shown.length === 0 && (
        <InfoWithCta
          icon={<SearchIcon size={"24px"} />}
          title={"Pusto!"}
          desc={"Brak wyników w tej lokalizacji."}
          cta={
            <Button
              onClick={() => {
                resetMap(true);
              }}
              variant={"blue"}
            >
              Oddal mapę
            </Button>
          }
        />
      )}
      {shown.length >= 24 && shown.length < facilities.length && (
        <div ref={ref}>
          <LoaderIcon className={"animate-spin"} size={16} />
        </div>
      )}
    </CardList>
  );
};
